/** Installer-side total + pay step for an order: estimate → office-confirmed amount → "I've paid" (how + reference). */
import React, { useState } from 'react';
import { BadgeCheck, Clock, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { BankDetails } from '@/components/orders/BankDetails';

const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n) || 0);
const METHODS = [['bank_transfer', 'Bank transfer'], ['card', 'Card'], ['go_cardless', 'GoCardless'], ['cash', 'Cash']];
const PRE_CONFIRM = ['draft', 'submitted', 'pending'];

/** Right-hand total column. Shows "Estimate" until the office confirms the invoice amount. */
export function OrderTotal({ order, appTotal, color, testId }) {
  const confirmed = order.confirmed_total != null;
  return (
    <div className="text-right" data-testid={testId}>
      {confirmed ? (
        <>
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Confirmed by Ox Resin</div>
          <div className="text-xl font-bold" style={{ color }}>{fmt(order.confirmed_total)}</div>
          {Math.abs(Number(order.confirmed_total) - Number(appTotal)) >= 0.01 && <div className="text-[10px] text-gray-400 line-through">est. {fmt(appTotal)}</div>}
        </>
      ) : (
        <>
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Estimate</div>
          <div className="text-xl font-bold text-gray-500">{fmt(appTotal)}</div>
          {PRE_CONFIRM.includes(order.status) && <div className="text-[10px] text-amber-700">to be confirmed</div>}
        </>
      )}
    </div>
  );
}

/** Action row: notice before confirmation, Mark as Paid (with how-paid dialog) once confirmed. */
export function InstallerPayStep({ order, onMarkPaid, testPrefix, business }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  const [tick, setTick] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmed = order.confirmed_total != null;

  const submit = async () => {
    if (!tick) { toast.error(`Please tick to confirm you've paid ${fmt(order.confirmed_total)}`); return; }
    setBusy(true);
    try { await onMarkPaid({ status: 'paid', payment_method: method, payment_reference: reference.trim(), paid_amount_confirmed: true }); setOpen(false); setTick(false); setReference(''); }
    finally { setBusy(false); }
  };

  if (PRE_CONFIRM.includes(order.status)) {
    return (
      <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid={`${testPrefix}-dont-pay-${order.reference}`}>
        <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span><strong>Please don't pay yet.</strong> The total above is the app's estimate — the Ox Resin office will check it and confirm the exact amount to pay. You'll get an email and the amount will show here as <em>Confirmed</em>.</span>
      </div>
    );
  }
  if (order.status === 'invoiced' && !confirmed) {
    return (
      <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid={`${testPrefix}-awaiting-amount-${order.reference}`}>
        <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span><strong>Invoice raised — amount being confirmed.</strong> Hold off paying until the office confirms the total; Mark as Paid unlocks then.</span>
      </div>
    );
  }
  if (order.status === 'invoiced') {
    return (
      <>
        <div className="w-full flex flex-wrap items-center gap-2">
          <span className="text-xs text-emerald-800 font-semibold inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Pay exactly {fmt(order.confirmed_total)}{order.invoice_number ? ` · INV ${order.invoice_number}` : ''}</span>
          <Button onClick={() => setOpen(true)} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white font-bold ml-auto" data-testid={`${testPrefix}-mark-paid-${order.reference}`}><BadgeCheck className="w-3 h-3 mr-1" /> I've paid — mark as paid</Button>
        </div>
        {business && <div className="w-full mt-2"><BankDetails business={business} reference={order.invoice_number || order.reference} compact testId={`${testPrefix}-bank-${order.reference}`} /></div>}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md" data-testid={`${testPrefix}-pay-dialog`}>
            <DialogHeader>
              <DialogTitle>Mark {order.reference} as paid</DialogTitle>
              <DialogDescription>Ox Resin confirmed the amount as <strong>{fmt(order.confirmed_total)}</strong>{order.invoice_number ? ` (invoice ${order.invoice_number})` : ''}. Tell us how you paid so the office can match it.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map(([k, l]) => <button key={k} type="button" onClick={() => setMethod(k)} aria-pressed={method === k} data-testid={`${testPrefix}-pay-method-${k}`} className={`h-10 rounded-lg border text-sm font-semibold ${method === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`}>{l}</button>)}
              </div>
              {method === 'bank_transfer' && business && <BankDetails business={business} reference={order.invoice_number || order.reference} compact testId={`${testPrefix}-pay-bank`} />}
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Payment reference (optional)" maxLength={80} data-testid={`${testPrefix}-pay-reference`} />
              <label className="flex items-start gap-2 text-sm text-slate-800 rounded-lg border border-slate-200 p-3 cursor-pointer">
                <input type="checkbox" checked={tick} onChange={(e) => setTick(e.target.checked)} className="mt-0.5" data-testid={`${testPrefix}-pay-tick`} />
                <span>I confirm I have paid <strong>{fmt(order.confirmed_total)}</strong> — the amount confirmed by the Ox Resin office — for this order.</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testPrefix}-pay-cancel`}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !tick} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`${testPrefix}-pay-submit`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark as paid'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return null;
}
